import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Divider, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import { Refresh, ThumbUp, ThumbDown, Feedback } from '@mui/icons-material';
import { gamoraApi, FeedbackItem } from '../services/api';

const FeedbackSystem: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

        const fetchAllFeedback = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch all feedback from the database
          const response = await gamoraApi.getFeedback();
          console.log('Feedback response:', response);
          console.log('Feedbacks with usernames:', response.feedbacks?.map(f => ({ 
            id: f.feedback_id, 
            username: f.username, 
            user_id: f.user_id 
          })));
          setFeedbacks(response.feedbacks || []);
        } catch (err) {
          setError('Failed to fetch feedback data. Please try again.');
          console.error('Error fetching feedbacks:', err);
        } finally {
          setLoading(false);
        }
      };

  useEffect(() => {
    fetchAllFeedback();
  }, []);



  const handleRefresh = () => {
    fetchAllFeedback();
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2, borderRadius: '10px' }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em' }}>
          User Feedback
        </Typography>
        
        <Tooltip title="Refresh feedback data">
          <IconButton
            onClick={handleRefresh}
            sx={{
              color: '#6b7280',
              width: 32,
              height: 32,
              borderRadius: '8px',
              '&:hover': { bgcolor: '#f3f4f6', color: '#111827' },
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography sx={{ mb: 3, fontSize: '0.8rem', color: '#9ca3af' }}>
        View real user feedback submitted on AI chat responses. This shows actual thumbs up/down feedback from users stored in the database.
      </Typography>

      {/* Feedback Statistics */}
      <Box
        sx={{
          border: '1px solid #eceef1',
          borderRadius: '12px',
          bgcolor: '#fff',
          p: 2.5,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6b7280', flexShrink: 0 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                {feedbacks.length}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total Feedback
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ bgcolor: '#eceef1' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                {feedbacks.filter(f => f.score === 1).length}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Positive
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ bgcolor: '#eceef1' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                {feedbacks.filter(f => f.score === 0).length}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Negative
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ bgcolor: '#eceef1' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                {feedbacks.length > 0 ? Math.round((feedbacks.filter(f => f.score === 1).length / feedbacks.length) * 100) : 0}%
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Satisfaction Rate
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feedback List */}
      {feedbacks.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px solid #eceef1',
            borderRadius: '12px',
            bgcolor: '#fff',
          }}
        >
          <Feedback sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }} gutterBottom>
            No feedback available yet
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
            User feedback will appear here once users start providing thumbs up/down on AI responses.
          </Typography>
        </Box>
      ) : (
        <Box>
          {feedbacks.map((feedback, index) => (
            <Box
              key={feedback.feedback_id || index}
              sx={{
                p: 2.5,
                mb: 2,
                border: '1px solid #eceef1',
                borderRadius: '12px',
                bgcolor: '#fff',
                borderLeft: '3px solid',
                borderLeftColor: feedback.score === 1 ? '#22c55e' : '#ef4444',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {feedback.score === 1 ? (
                    <ThumbUp sx={{ fontSize: 20, color: '#22c55e' }} />
                  ) : (
                    <ThumbDown sx={{ fontSize: 20, color: '#ef4444' }} />
                  )}

                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>
                      {feedback.score === 1 ? 'Positive Feedback' : 'Negative Feedback'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                      {feedback.feedback_type === 'thumbs_up' ? 'User liked this response' : 'User disliked this response'}
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                  {formatTimestamp(feedback.timestamp)}
                </Typography>
              </Box>

              {feedback.comment && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', mb: 0.5 }} gutterBottom>
                    User Comment:
                  </Typography>
                  <Box sx={{ bgcolor: '#f8f9fb', borderRadius: '8px', p: 1.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>
                      {feedback.comment}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {feedback.generation_id && (
                  <Chip
                    label={`Generation ID: ${feedback.generation_id}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      bgcolor: '#f8f9fb',
                      color: '#374151',
                    }}
                  />
                )}
                {feedback.feedback_id && (
                  <Chip
                    label={`Feedback ID: ${feedback.feedback_id}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      bgcolor: '#f8f9fb',
                      color: '#374151',
                    }}
                  />
                )}
                {feedback.username && (
                  <Chip
                    label={`User: ${feedback.username}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      bgcolor: '#f8f9fb',
                      color: '#374151',
                    }}
                  />
                )}
                {feedback.user_id && !feedback.username && (
                  <Chip
                    label={`User ID: ${feedback.user_id}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      bgcolor: '#f8f9fb',
                      color: '#374151',
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FeedbackSystem;
